import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Share2, Copy, Check, Link2, QrCode, Facebook, Linkedin,
  Mail, MessageSquare, Smartphone, Globe, ChevronDown, ChevronUp,
  ExternalLink, Sparkles, Image as ImageIcon, ContactRound, Download, Radio,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { buildVCard, downloadVCard } from "@/utils/vcard";
import { buildListingShareUrl } from "@/utils/shareUrl";
import SocialShareSection from "@/components/dashboard/SocialShareSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
const SITE_URL = "https://localbest.ai";

const UTM_PRESETS = [
  { label: "Facebook Post", source: "facebook", medium: "social", campaign: "listing_share" },
  { label: "Instagram Bio", source: "instagram", medium: "social", campaign: "bio_link" },
  { label: "LinkedIn Post", source: "linkedin", medium: "social", campaign: "listing_share" },
  { label: "Google Business Profile", source: "google", medium: "organic", campaign: "gbp_post" },
  { label: "Email Campaign", source: "email", medium: "email", campaign: "listing_promo" },
  { label: "SMS/Text Message", source: "sms", medium: "sms", campaign: "text_blast" },
  { label: "QR Code (Print)", source: "qrcode", medium: "print", campaign: "flyer" },
  { label: "Paid Ad", source: "paid", medium: "cpc", campaign: "listing_ad" },
];

export default function SharePromote() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState(null);
  const [copied, setCopied] = useState(null);
  const [showSuggested, setShowSuggested] = useState(true);
  const [showQr, setShowQr] = useState(false);

  // UTM builder state
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [utmContent, setUtmContent] = useState("");
  const [utmTerm, setUtmTerm] = useState("");

  useEffect(() => { fetchListing(); }, []);

  const fetchListing = async () => {
    try {
      const res = await axios.get(`${API}/listings/my`, { headers: getAuthHeader() });
      setListing(res.data);
    } catch (err) {
      console.error("Failed to fetch listing:", err);
    } finally {
      setLoading(false);
    }
  };

  const getCleanUrl = useCallback(() => {
    if (!listing) return SITE_URL;
    // Use the backend share URL (api/og/directory/...) so crawlers see the
    // correct per-listing OG/Twitter meta tags. Real users are auto-redirected
    // to the public listing page after the page loads.
    return buildListingShareUrl(listing, { origin: SITE_URL });
  }, [listing]);

  const getUtmUrl = useCallback((source, medium, campaign, content, term) => {
    const base = getCleanUrl();
    const params = new URLSearchParams();
    if (source) params.set("utm_source", source);
    if (medium) params.set("utm_medium", medium);
    if (campaign) params.set("utm_campaign", campaign);
    if (content) params.set("utm_content", content);
    if (term) params.set("utm_term", term);
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }, [getCleanUrl]);

  const customUtmUrl = getUtmUrl(utmSource, utmMedium, utmCampaign, utmContent, utmTerm);

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      toast.success(`${label} copied!`);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const getQrUrl = (url) => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(url)}&bgcolor=0F172A&color=FFFFFF&margin=10`;
  };

  const bizName = listing?.name || "My Business";
  const category = listing?.category_name || "business";
  const city = listing?.city || "";
  const state = listing?.state || "";
  const location = [city, state].filter(Boolean).join(", ");

  // Suggested captions
  const suggestedCaptions = [
    {
      platform: "Facebook / Instagram",
      icon: <Facebook className="w-4 h-4" />,
      text: `Check out ${bizName} on LocalBest.ai — The AI Horse-Powered Best of Best Directory! Trusted ${category.toLowerCase()} in ${location || "your area"}. See reviews, get directions, and connect instantly.`,
    },
    {
      platform: "LinkedIn",
      icon: <Linkedin className="w-4 h-4" />,
      text: `Proud to be listed on LocalBest.ai as a top ${category.toLowerCase()} in ${location || "the area"}. Visit our listing to learn more about our services and connect with us directly.`,
    },
    {
      platform: "Email Subject Line",
      icon: <Mail className="w-4 h-4" />,
      text: `${bizName} is now featured on LocalBest.ai`,
    },
    {
      platform: "SMS / Text",
      icon: <MessageSquare className="w-4 h-4" />,
      text: `Check out ${bizName} on LocalBest.ai! ${getCleanUrl()}`,
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="text-center py-20">
        <Share2 className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">No Listing Found</h2>
        <p className="text-[#94A3B8]">Create or claim a listing first to use Share & Promote.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="share-promote-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">Share & Promote</h1>
        <p className="text-[#94A3B8] mt-1">Generate tracking links, QR codes, and social copy for <span className="text-white font-medium">{bizName}</span></p>
      </div>

      {/* === Social Share (Verified Badge card + share to all platforms) === */}
      <SocialShareSection listing={listing} />

      {/* Clean URL */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <Link2 className="w-5 h-5 text-[#7C3AED]" /> Your Listing URL
        </h2>
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#E2E8F0] font-mono text-sm truncate" data-testid="clean-listing-url">
            {getCleanUrl()}
          </div>
          <Button
            variant="outline"
            onClick={() => copyToClipboard(getCleanUrl(), "Clean URL")}
            className="border-white/10 text-white hover:bg-white/5 flex-shrink-0"
            data-testid="copy-clean-url"
          >
            {copied === "Clean URL" ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4" />}
          </Button>
          <a href={getCleanUrl()} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="border-white/10 text-white hover:bg-white/5 flex-shrink-0" data-testid="preview-listing-link">
              <ExternalLink className="w-4 h-4" />
            </Button>
          </a>
        </div>
      </motion.div>

      {/* Quick UTM Presets */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#F59E0B]" /> Quick Tracking Links
        </h2>
        <p className="text-sm text-[#94A3B8] mb-4">One-click copy links with UTM attribution for each platform. Every click back to your listing will be tracked.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {UTM_PRESETS.map((preset) => {
            const url = getUtmUrl(preset.source, preset.medium, preset.campaign, "", "");
            const label = `UTM:${preset.label}`;
            return (
              <div key={preset.label} className="flex items-center justify-between bg-white/5 border border-white/5 rounded-lg px-4 py-3 hover:border-[#7C3AED]/30 transition-colors">
                <div>
                  <span className="text-white text-sm font-medium">{preset.label}</span>
                  <p className="text-[#64748B] text-xs mt-0.5">
                    {preset.source} / {preset.medium}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(url, label)}
                  className="text-[#94A3B8] hover:text-white"
                  data-testid={`copy-preset-${preset.source}`}
                >
                  {copied === label ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Custom UTM Builder */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-[#00E5FF]" /> Custom UTM Builder
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="text-xs text-[#94A3B8] mb-1 block">Source *</label>
            <Input value={utmSource} onChange={(e) => setUtmSource(e.target.value)} placeholder="e.g. facebook, google, newsletter" className="bg-white/5 border-white/10 text-white placeholder:text-[#475569]" data-testid="utm-source-input" />
          </div>
          <div>
            <label className="text-xs text-[#94A3B8] mb-1 block">Medium *</label>
            <Input value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} placeholder="e.g. social, cpc, email" className="bg-white/5 border-white/10 text-white placeholder:text-[#475569]" data-testid="utm-medium-input" />
          </div>
          <div>
            <label className="text-xs text-[#94A3B8] mb-1 block">Campaign *</label>
            <Input value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} placeholder="e.g. spring_promo, best_of_best" className="bg-white/5 border-white/10 text-white placeholder:text-[#475569]" data-testid="utm-campaign-input" />
          </div>
          <div>
            <label className="text-xs text-[#94A3B8] mb-1 block">Content</label>
            <Input value={utmContent} onChange={(e) => setUtmContent(e.target.value)} placeholder="e.g. hero_banner, sidebar_cta" className="bg-white/5 border-white/10 text-white placeholder:text-[#475569]" data-testid="utm-content-input" />
          </div>
          <div>
            <label className="text-xs text-[#94A3B8] mb-1 block">Term</label>
            <Input value={utmTerm} onChange={(e) => setUtmTerm(e.target.value)} placeholder="e.g. roofing, best contractor" className="bg-white/5 border-white/10 text-white placeholder:text-[#475569]" data-testid="utm-term-input" />
          </div>
        </div>
        {(utmSource || utmMedium || utmCampaign) && (
          <div className="bg-white/5 border border-white/10 rounded-lg p-3">
            <p className="text-xs text-[#64748B] mb-1">Generated URL:</p>
            <div className="flex items-center gap-2">
              <p className="text-sm text-[#E2E8F0] font-mono truncate flex-1" data-testid="custom-utm-url">{customUtmUrl}</p>
              <Button size="sm" variant="ghost" onClick={() => copyToClipboard(customUtmUrl, "Custom UTM")} className="text-[#94A3B8] hover:text-white flex-shrink-0" data-testid="copy-custom-utm">
                {copied === "Custom UTM" ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        )}
      </motion.div>

      {/* QR Code */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
        <button onClick={() => setShowQr(!showQr)} className="flex items-center justify-between w-full" data-testid="toggle-qr-section">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <QrCode className="w-5 h-5 text-[#7C3AED]" /> QR Codes
          </h2>
          {showQr ? <ChevronUp className="w-5 h-5 text-[#94A3B8]" /> : <ChevronDown className="w-5 h-5 text-[#94A3B8]" />}
        </button>
        {showQr && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Clean URL QR */}
            <QrCard
              title="Clean URL"
              subtitle="Use on business cards, signage"
              url={getCleanUrl()}
              qrUrl={getQrUrl(getCleanUrl())}
              onCopy={() => copyToClipboard(getCleanUrl(), "QR Clean")}
              copied={copied === "QR Clean"}
            />
            {/* Social QR */}
            <QrCard
              title="Social Share QR"
              subtitle="Use on flyers, social posts"
              url={getUtmUrl("qrcode", "social", "listing_share", "", "")}
              qrUrl={getQrUrl(getUtmUrl("qrcode", "social", "listing_share", "", ""))}
              onCopy={() => copyToClipboard(getUtmUrl("qrcode", "social", "listing_share", "", ""), "QR Social")}
              copied={copied === "QR Social"}
            />
            {/* Print QR */}
            <QrCard
              title="Print Campaign QR"
              subtitle="Use on direct mail, print ads"
              url={getUtmUrl("qrcode", "print", "flyer", "", "")}
              qrUrl={getQrUrl(getUtmUrl("qrcode", "print", "flyer", "", ""))}
              onCopy={() => copyToClipboard(getUtmUrl("qrcode", "print", "flyer", "", ""), "QR Print")}
              copied={copied === "QR Print"}
            />
          </motion.div>
        )}
      </motion.div>

      {/* === Digital Business Card / vCard === */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6" data-testid="vcard-section">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <ContactRound className="w-5 h-5 text-[#10B981]" /> Digital Business Card / vCard
          </h2>
        </div>
        <p className="text-sm text-[#94A3B8] mb-5">Download a contact file (.vcf) anyone can save to their phone in one tap. Includes your name, phone, email, website, and LocalBest listing URL.</p>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => {
              if (!listing) return;
              const vcard = buildVCard({
                name: listing.name,
                title: listing.is_individual ? (listing.category_name || "Pro") : undefined,
                company: listing.is_individual ? (listing.company_name || "") : undefined,
                phone: listing.contact_phone || listing.phone_primary,
                email: listing.contact_email || listing.email,
                website: listing.website,
                listingUrl: getCleanUrl(),
                address: listing.street_address,
                city: listing.city,
                state: listing.state,
                zip: listing.zip_code,
                logoUrl: listing.logo_url,
                category: listing.category_name,
                isIndividual: !!listing.is_individual,
              });
              const safe = (listing.name || "contact").replace(/[^a-z0-9]+/gi, "_");
              downloadVCard(safe, vcard);
              toast.success("Contact card downloaded");
            }}
            className="bg-[#10B981] hover:bg-[#059669] text-white"
            data-testid="vcard-download-btn"
          >
            <Download className="w-4 h-4 mr-2" /> Download Contact Card (.vcf)
          </Button>
          <span className="text-xs text-[#64748B]">Works on iPhone, Android, Outlook, Google Contacts, etc.</span>
        </div>
      </motion.div>

      {/* === NFC / Tap Card URL === */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6" data-testid="nfc-section">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Radio className="w-5 h-5 text-[#F59E0B]" /> NFC / Tap Card URL
          </h2>
        </div>
        <p className="text-sm text-[#94A3B8] mb-5">Use this URL to program an NFC card, tap card, sticker, or smart business card that opens your Best of Best Listing when tapped.</p>
        <div className="flex gap-2">
          <Input
            readOnly
            value={getUtmUrl("nfc", "tap_card", "tap_open", "", "")}
            className="bg-white/5 border-white/10 text-white font-mono text-sm"
            data-testid="nfc-url-input"
          />
          <Button
            onClick={() => copyToClipboard(getUtmUrl("nfc", "tap_card", "tap_open", "", ""), "NFC URL")}
            variant="outline"
            className="border-white/10 bg-white/5 hover:bg-white/10 text-white"
            data-testid="nfc-copy-btn"
          >
            {copied === "NFC URL" ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-[11px] text-[#64748B] mt-3 italic">
          Future product: order a LocalBest Tap Card pre-programmed with your listing.
        </p>
      </motion.div>

      {/* Suggested Captions */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
        <button onClick={() => setShowSuggested(!showSuggested)} className="flex items-center justify-between w-full" data-testid="toggle-captions">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#10B981]" /> Suggested Copy
          </h2>
          {showSuggested ? <ChevronUp className="w-5 h-5 text-[#94A3B8]" /> : <ChevronDown className="w-5 h-5 text-[#94A3B8]" />}
        </button>
        {showSuggested && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4 mt-6">
            {suggestedCaptions.map((cap, i) => (
              <div key={cap.platform || `cap-${i}`} className="bg-white/5 border border-white/5 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-[#94A3B8]">
                    {cap.icon}
                    <span className="text-sm font-medium">{cap.platform}</span>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(cap.text, `Caption:${cap.platform}`)}
                    className="text-[#94A3B8] hover:text-white"
                    data-testid={`copy-caption-${i}`}
                  >
                    {copied === `Caption:${cap.platform}` ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-[#E2E8F0] text-sm leading-relaxed">{cap.text}</p>
              </div>
            ))}
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}

function QrCard({ title, subtitle, url, qrUrl, onCopy, copied }) {
  return (
    <div className="bg-white/5 border border-white/5 rounded-xl p-4 flex flex-col items-center text-center">
      <div className="w-[200px] h-[200px] bg-[#0F172A] rounded-lg flex items-center justify-center mb-3 overflow-hidden">
        <img src={qrUrl} alt={`QR Code for ${title}`} className="w-full h-full object-contain" loading="lazy" />
      </div>
      <h3 className="text-white text-sm font-semibold">{title}</h3>
      <p className="text-[#64748B] text-xs mt-0.5 mb-3">{subtitle}</p>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onCopy} className="border-white/10 text-white hover:bg-white/5 text-xs">
          {copied ? <Check className="w-3 h-3 mr-1 text-[#10B981]" /> : <Copy className="w-3 h-3 mr-1" />}
          Copy Link
        </Button>
        <a href={qrUrl} download={`qr-${title.toLowerCase().replace(/\s+/g, '-')}.png`}>
          <Button size="sm" variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs">
            <ImageIcon className="w-3 h-3 mr-1" /> Save QR
          </Button>
        </a>
      </div>
    </div>
  );
}
